import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  Alert,
  Button,
  Input,
  Label,
  Modal,
  ModalFooter,
} from "@/components/ui";
import { getApiErrorMessage } from "@/lib/errorMessages";
import {
  formatReaderSerialNumber,
  normalizeReaderSerialNumber,
} from "@/lib/readerSerialNumber";
import { readerService } from "@/services/readerService";
import type { ReaderInventory } from "@/types";

interface RenameReaderModalProps {
  isOpen: boolean;
  reader: ReaderInventory | null;
  onClose: () => void;
  onRenamed: () => Promise<void>;
}

export function RenameReaderModal({
  isOpen,
  reader,
  onClose,
  onRenamed,
}: RenameReaderModalProps) {
  const { t } = useTranslation("readers");
  const { t: tCommon } = useTranslation("common");
  const [serialNumber, setSerialNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !reader) return;
    setSerialNumber(
      formatReaderSerialNumber(reader.serial_number, reader.provider),
    );
    setError(null);
  }, [isOpen, reader]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reader) return;

    const nextSerial = normalizeReaderSerialNumber(serialNumber);
    if (!nextSerial) {
      setError(t("renameModal.serialRequired"));
      return;
    }
    if (nextSerial === reader.serial_number) {
      onClose();
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await readerService.update(reader.id, { serial_number: nextSerial });
      toast.success(t("page.readerRenamed"));
      await onRenamed();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, "readerOperation"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen={isOpen && reader != null}
      onClose={onClose}
      title={t("renameModal.title")}
      size="sm"
      footer={
        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {tCommon("cancel")}
          </Button>
          <Button
            type="submit"
            form="rename-reader-form"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {tCommon("save")}
          </Button>
        </ModalFooter>
      }
    >
      {reader ? (
        <form
          id="rename-reader-form"
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          <p className="text-sm text-surface-600">
            {t("renameModal.help", {
              serial: formatReaderSerialNumber(
                reader.serial_number,
                reader.provider,
              ),
            })}
          </p>
          <div>
            <Label htmlFor="renameSerialNumber">
              {t("renameModal.serialLabel")}
            </Label>
            <Input
              id="renameSerialNumber"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder={t("renameModal.serialPlaceholder")}
              autoComplete="off"
              disabled={isLoading}
              required
              className="mt-2"
            />
          </div>
          {error ? <Alert variant="error">{error}</Alert> : null}
        </form>
      ) : null}
    </Modal>
  );
}
